<script setup>
import { ref, reactive } from "vue";
import { useUserStore } from "@store/user";
import CommentsApi from "@/api/comments.api";
import sendIcon from "@/assets/img/send.png";
import SendCommentIcon from "@/assets/svg/send-comment-icon.svg?component";
import { useValidation } from "@composables/useValidation";
import { required, maxLength } from "@services/i18n-validators";
import { ElNotification } from "element-plus";

const props = defineProps({
    type: { type: String, required: true }, // 'project' or 'document'
    id: { type: [Number, String], required: true },
    placeholder: { type: String, default: "Add Comment" },
});

const emit = defineEmits(["comment-added"]);

const userStore = useUserStore();
const form = reactive({ context: "" });
const { v$, $externalResults } = useValidation(
    reactive({ context: { required, maxLength: maxLength(1000) } }),
    form
);
const isSubmitting = ref(false);

async function addNewComment() {
    const isValid = await v$.value.$validate();

    if (!isValid) {
        return;
    }

    const text = (form.context || "").trim();
    if (!text || isSubmitting.value) return;

    isSubmitting.value = true;

    try {
        const res = await CommentsApi.add(props.type, props.id, {
            context: text,
        });

        const fallbackUser = {
            name: userStore?.name || userStore?.user?.name || "You",
        };

        const created = res?.data?.comment ||
            res?.data ||
            res || {
                id: Date.now(),
                context: text,
                created_at: new Date().toISOString(),
                user: fallbackUser,
                replies: [],
            };

        const comment = {
            id: created.id,
            page_number: created.page_number || 1,
            context: created.context,
            created_at: created.created_at,
            is_resolved: !!created.is_resolved,
            user: {
                name: created.user?.name || fallbackUser.name,
                avatar: { url: created.user?.user_avatar?.url || null },
            },
            replies: [],
        };

        form.context = "";
        if (v$ && v$.value) v$.value.$reset();
        emit("comment-added", comment);
    } catch (error) {
        console.error("Error adding comment:", error);
    } finally {
        isSubmitting.value = false;
    }
}
</script>

<template>
    <div class="add-reply">
        <div class="input-wrap">
            <input
                v-model="form.context"
                type="text"
                class="reply-input"
                :placeholder="placeholder"
                @keyup.enter="addNewComment"
                :disabled="isSubmitting"
            />
            <div v-if="v$?.context?.$error" class="error-text">
                <small
                    v-for="e in v$.context.$errors"
                    :key="e.$uid"
                    class="text-danger"
                >
                    {{ e.$message }}
                </small>
            </div>
        </div>
        <button
            class="send-btn"
            @click="addNewComment"
            :disabled="isSubmitting"
        >
            <SendCommentIcon />
            <!-- <img :src="sendIcon" alt="send" /> -->
        </button>
    </div>
</template>

<style scoped>
.add-reply {
    display: flex;
    align-items: center;
    gap: 8px;
}
.input-wrap {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
}
.error-text {
    margin-top: 4px;
    line-height: 1.2;
}
.error-text .text-danger {
    font-size: 0.8rem;
    display: block;
}
</style>
